import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { InteractionManager, StyleSheet, View, } from 'react-native';
import { truncate } from 'lodash';
import Animated from 'react-native-reanimated';
import { differenceInDays } from 'date-fns';
import { resetInvestmentBalances, getAccountGoals, isFetchingBankTransactions, getTransactions, getTransactionSchedulesForAccount, } from 'actions';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { AppView } from 'design-system/AppView';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconTrueBalance from 'design-system/icons/IconTrueBalance';
import StatusBar from 'design-system/StatusBar';
import UpgradeCard from 'design-system/UpgradeCard';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import AccountsHeaderRight from 'features/accounts/components/header/AccountsHeaderRight';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { fetchCommittedForCurrentPaydayRange } from 'features/subscriptions/actions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectAccountById, selectConnectionById, selectCurrentPaydayRangeWithFallback, selectFeatureFlag, selectIsMyAccount, } from 'reducers/selectors';
import { selectShowNewPhysicalAssets, selectShowTrueBalance, } from 'features/accounts/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import useFocusEffect from 'hooks/useFocusEffect';
import useIsTablet from 'hooks/useIsTablet';
import OpenPotsAccountNotificationBanner from 'features/pots/components/OpenPotsAccountNotificationBanner';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { CardView, Indicator, Spacer, TextButtonLinkSeeAll, } from 'design-system';
import ListEmptyView from 'design-system/ListEmptyView';
import RowTransaction from 'features/transactions/components/RowTransaction';
import Section from 'design-system/Section';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import NavigationHeaderTitle from 'design-system/NavigationHeaderTitle';
import { HEADER_WITH_SUBTITLE_HEIGHT } from 'navigators/styles';
import { isBankAccountBroken, isConnectionBroken, } from 'features/settings/logins/utils';
import AlertViewNew from 'design-system/AlertViewNew';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useOpenConnectionLogin } from 'hooks/useOpenConnectionLogin';
import ScheduledTransactionRow from 'features/transactions/components/ScheduledTransactionRow';
import { selectTransactionsSchedules } from 'features/transactions/selectors';
import SwitchingButton from 'features/subscriptions/components/row/SwitchingButton';
import { Amplitude } from 'utils';
import useNavigateTransactionItem from 'features/transactions/hooks/useNavigateTransactionItem';
import AccountGoalsSection from '../components/header/AccountGoalsSection';
import AccountHeaderBalance from '../components/header/AccountHeaderBalance';
import useInvestmentGraphBalanceHistory from '../hooks/useInvestmentGraphBalanceHistory';
import { isWeb } from '../../../constants';
import ConsolidateDebtBanner from '../components/header/ConsolidateDebtBanner';
import AccountsInvestmentHeader from '../components/header/AccountInvestmentsHeader';
import SmartRuleSection from '../components/SmartRuleSection';
import AccountPaySection from '../components/header/AccountPaySection';
import { getPropertyName } from '../constants';
const AccountOverviewScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const navigateToTransactionItem = useNavigateTransactionItem();
    const showTrueBalance = useAppSelector(selectShowTrueBalance);
    useEffect(() => {
        const interaction = InteractionManager.runAfterInteractions(() => {
            if (!store.getState().accounts.loadedGoals) {
                dispatch(getAccountGoals());
            }
        });
        return () => interaction.cancel();
    }, []);
    const colors = useColors();
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigate(routeWithParams);
        Amplitude.logEvent('Transactions.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const data = useAppSelector((store) => selectAccountById(store, route.params.id));
    const isMyAccount = useAppSelector((store) => selectIsMyAccount(store, data?.userId));
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const isPhysicalAsset = !!data?.isManual &&
        (data?.type === 'REAL_ESTATE' ||
            data?.type === 'VEHICLE' ||
            data?.type === 'OTHER');
    const showInvestmentHeader = (isPhysicalAsset && showNewPhysicalAssets) ||
        data?.type === 'INVESTMENT' ||
        (data?.type === 'PENSION' && showNewPhysicalAssets) ||
        (data?.type === 'CRYPTO' && data?.nativeBalance);
    const isTablet = useIsTablet();
    useLayoutEffect(() => {
        const opts = {};
        if (data) {
            if (isWeb) {
                navigation.setParams({ data });
            }
            opts.headerRight = () => (<AccountsHeaderRight account={data} navigation={navigation}/>);
            opts.headerTitle = () => (<NavigationHeaderTitle title={isTablet || isWeb
                    ? data?.name
                    : truncate(data?.name, { length: 24 })}>
            <SpaceUserAvatar xxSmall userId={data.userId}/>
          </NavigationHeaderTitle>);
        }
        navigation.setOptions({
            ...opts,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, data, isTablet, showInvestmentHeader]);
    useEffect(() => {
        const unsubscribe = navigation.addListener('beforeRemove', () => {
            dispatch(resetInvestmentBalances());
        });
        return unsubscribe;
    }, []);
    const forceRefresh = useAppSelector((store) => store.expenses.forceRefresh);
    const currentPaydayRange = useAppSelector(selectCurrentPaydayRangeWithFallback);
    const loadInvestmentGraphBalanceHistory = useInvestmentGraphBalanceHistory();
    useEffect(() => {
        const interaction = InteractionManager.runAfterInteractions(() => {
            if (showInvestmentHeader) {
                loadInvestmentGraphBalanceHistory(data.id);
            }
        });
        return () => interaction.cancel();
    }, [
        data?.id,
        loadInvestmentGraphBalanceHistory,
        showInvestmentHeader,
        data?.availableBalance,
        data?.postedBalance,
        data?.nativeBalance?.availableBalance,
        data?.nativeBalance?.postedBalance,
        data?.transactionsCount,
    ]);
    const accountId = route.params.id || data?.id;
    useFocusEffect(useCallback(() => {
        const interaction = InteractionManager.runAfterInteractions(() => {
            dispatch(fetchCommittedForCurrentPaydayRange());
        });
        return () => interaction.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPaydayRange, forceRefresh]));
    const addTransaction = useCallback(() => {
        if (data) {
            navigation.navigate('AddAmount', {
                data,
            });
        }
    }, [data]);
    const updateValuation = useCallback(() => {
        if (data) {
            navigation.navigate('SelectDate', {
                data,
                longTitle: 'When did the valuation change?',
                buttonText: 'Continue',
                onSubmitDate: (date) => {
                    navigation.navigate('AddValuation', {
                        data,
                        date,
                        keyBack: route.key,
                    });
                },
            });
        }
    }, [data, route.key]);
    const { isUnlockedForSpace: isTrueBalanceUnlocked } = useShouldShowUpgrade({
        benefitId: 'trueBalance',
    });
    const onPressUpgradeTrueBalance = useCallback(() => {
        navigation.navigate('TrueBalanceIntro', { id: route.params.id });
    }, [route.params.id]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const transactionsArray = useAppSelector((store) => store.transactions.transactionsArray);
    const transactionsScheduleArray = useAppSelector(selectTransactionsSchedules);
    const isFetching = useAppSelector((store) => store.transactions.isFetching);
    const isFetchingScheduled = useAppSelector((store) => store.transactions.isFetchingScheduled);
    const isRecurringOfflineTransactionsEnabled = useAppSelector((state) => selectFeatureFlag(state, 'recurring_offline_transactions_v2').value);
    const shouldShowScheduledTransactions = useMemo(() => isRecurringOfflineTransactionsEnabled && data?.provider === 'MANUAL', [isRecurringOfflineTransactionsEnabled, data?.provider]);
    useEffect(() => {
        const interaction = InteractionManager.runAfterInteractions(() => {
            dispatch(isFetchingBankTransactions('ALL', undefined, true, `accountIds[]=${route.params.id}&`));
            dispatch(getTransactions({
                accountIdsStr: `accountIds[]=${route.params.id}&`,
                page: 1,
                hasInternalTransactions: false,
            }));
        });
        return () => interaction.cancel();
    }, [
        route.params.id,
        data?.availableBalance,
        data?.postedBalance,
        data?.nativeBalance?.availableBalance,
        data?.nativeBalance?.postedBalance,
        data?.transactionsCount,
    ]);
    useEffect(() => {
        const interaction = InteractionManager.runAfterInteractions(() => {
            if (shouldShowScheduledTransactions && data) {
                dispatch(getTransactionSchedulesForAccount({
                    accountId: data.id,
                }));
            }
        });
        return () => interaction.cancel();
    }, [shouldShowScheduledTransactions, data]);
    const [hasHeaderElements, setHasHeaderElements] = useState(true);
    const onLayout = useCallback((event) => {
        if (!event.nativeEvent.layout.height) {
            setHasHeaderElements(false);
        }
    }, []);
    const emptyOrLoading = useMemo(() => (<View>
          {isFetching || isFetchingScheduled ? (<>
              <Spacer h={64}/>
              <Indicator dark/>
            </>) : (<>
              {hasHeaderElements ? <Spacer.H16 /> : null}
              <ListEmptyView onCard title={isPhysicalAsset && showNewPhysicalAssets
                ? 'No valuation changes'
                : 'No transactions'} subTitle={isPhysicalAsset && showNewPhysicalAssets
                ? `No change in ${getPropertyName(data?.type).toLowerCase()} valuation`
                : 'There are no transactions in your account'}/>
            </>)}
        </View>), [
        data?.type,
        hasHeaderElements,
        isFetching,
        isFetchingScheduled,
        isPhysicalAsset,
        showNewPhysicalAssets,
    ]);
    const openItem = useCallback((item) => {
        navigateToTransactionItem(item);
    }, [navigateToTransactionItem]);
    const openItemSchedule = useCallback((id) => {
        if (!data?.currency) {
            return;
        }
        navigation.navigate('ScheduledTransaction', {
            accountId: route.params.id,
            scheduleId: id,
            currency: data?.currency || '',
        });
    }, [data?.currency, route.params.id]);
    const onSeeAll = useCallback(() => {
        if (accountId) {
            navigation.navigate('TransactionsList', {
                filters: {
                    showExcludedTxns: true,
                    selectedAccountsObj: { [accountId]: true },
                },
            });
        }
    }, [accountId]);
    if (!data)
        return null;
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle}>
          {!showInvestmentHeader && <AccountHeaderBalance account={data}/>}
          <View style={styles.headerElements} onLayout={onLayout}>
            {showInvestmentHeader ? (<AccountsInvestmentHeader data={data}/>) : null}
            <AccountAlert data={data}/>
            {isMyAccount ? <AccountPaySection account={data}/> : null}
            {(data.type === 'SAVINGS' || showInvestmentHeader) &&
            !isPhysicalAsset &&
            isMyAccount ? (<AccountGoalsSection accountId={data.id}/>) : null}
            {data.provider === 'MANUAL' && !isPhysicalAsset && isMyAccount ? (<SmartRuleSection account={data}/>) : null}
            {!isWeb && data.type === 'CREDITCARD' && <ConsolidateDebtBanner />}
            {!isWeb && data.type === 'SAVINGS' && (<OpenPotsAccountNotificationBanner from="Transactions" noMarginTop noMarginBottom buttonText="Continue"/>)}
            {(!showTrueBalance || !isTrueBalanceUnlocked) &&
            data.type === 'CHECKING' ? (<UpgradeCard noMarginBottom btnText="Enable true balance" icon={<IconTrueBalance gradientKey="lightPink"/>} title="See your true balance" description="Let us account for your recurring payments so you can see the actual available amount you can spend." onPressUpgrade={onPressUpgradeTrueBalance}/>) : null}
          </View>
          {data?.currency &&
            transactionsScheduleArray?.length > 0 &&
            shouldShowScheduledTransactions ? (<Section title="Scheduled transactions" titleContainerStyle={[
                styles.sectionHeader,
                hasHeaderElements && styles.sectionHeaderLarge,
            ]}>
              <CardView noMarginTop noMarginBottom>
                {transactionsScheduleArray.map((transaction) => (<View key={transaction.id} style={styles.webPadding}>
                    <ScheduledTransactionRow schedule={transaction} backgroundDark onPress={openItemSchedule}/>
                  </View>))}
              </CardView>
            </Section>) : null}
          {transactionsArray?.length ? (<Section title={isPhysicalAsset && showNewPhysicalAssets
                ? 'Valuation changes'
                : 'Transactions'} button={<TextButtonLinkSeeAll onPress={onSeeAll}/>} titleContainerStyle={[
                styles.sectionHeader,
                hasHeaderElements && styles.sectionHeaderLarge,
            ]}>
              <CardView noMarginTop noMarginBottom>
                {transactionsArray.slice(0, 5).map((transaction) => (<View key={transaction.id} style={styles.webPadding}>
                    <RowTransaction backgroundDark onPress={openItem} item={transaction} switchingButon={<SwitchingButton data={transaction && transaction.merchant
                        ? { merchantInfo: transaction.merchant }
                        : null} onOpenCompare={onCompare} guessedHomeCountry={guessedHomeCountry}/>}/>
                  </View>))}
              </CardView>
            </Section>) : (emptyOrLoading)}
        </Animated.ScrollView>
        {data.provider === 'MANUAL' && isMyAccount ? (<Button brand floating onPress={isPhysicalAsset && showNewPhysicalAssets
                ? updateValuation
                : addTransaction} title={isPhysicalAsset && showNewPhysicalAssets
                ? 'Add valuation change'
                : 'Add transaction'}/>) : null}
      </AppView>);
}, ({ colors, route }) => ({
    headerHeight: HEADER_WITH_SUBTITLE_HEIGHT,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    hasCustomTitle: true,
    ...(isWeb && {
        title: route.params.data?.name || 'Account',
    }),
}));
const AccountAlert = ({ data }) => {
    const navigation = useMainStackNavigation();
    const connection = useAppSelector((store) => selectConnectionById(store, data?.bankConnectionId));
    const daysLeft = useMemo(() => connection?.consentExpiresAt
        ? differenceInDays(connection?.consentExpiresAt, new Date())
        : null, [connection?.consentExpiresAt]);
    const login = useOpenConnectionLogin(navigation);
    const onPressWarning = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://broken-connections', 'Renew access');
            return;
        }
        if (connection && isConnectionBroken(connection)) {
            navigation.navigate('BrokenConnections');
        }
        else {
            login({ type: 'account', data });
        }
    }, [connection, data, login]);
    const onPressReconsent = useCallback(() => {
        navigation.navigate('LoginsReconsent', { isModal: true });
    }, []);
    if (isBankAccountBroken(data)) {
        return (<AlertViewNew type="warning" title="Action required" subTitle={`We need your help to get the latest data from ${data.bankName}`} action="Tap to resolve" onPress={onPressWarning} style={styles.alert}/>);
    }
    if (connection?.consentExpiresAt && daysLeft && daysLeft <= 14) {
        return (<AlertViewNew type="info" withIconInfo title="Connection expiring" subTitle={`${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left. Tap to extend`} onPress={onPressReconsent} style={styles.alert}/>);
    }
    return null;
};
const styles = StyleSheet.create({
    webPadding: {
        ...(isWeb && {
            padding: rem(8),
        }),
    },
    alert: {
        marginTop: 0,
        marginBottom: 0,
    },
    headerElements: {
        gap: rem(16),
    },
    sectionHeader: {
        paddingTop: rem(8),
    },
    sectionHeaderLarge: {
        paddingTop: rem(24),
    },
});
export default AccountOverviewScreen;
